import {Box, Grid, Typography, Chip, Collapse, Button} from '@mui/material'
import {useState, useEffect} from 'react'
import {useForm} from 'react-hook-form'
import _ from 'lodash'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import {onlyUnique} from '../../helpers/UtilityHelper'
import {DownloadExcelMultipleSheets} from '../../helpers/ExcelGenerator'
import {apiConstants, get} from '../../common/constants'
import {FormProvider, RHFSelectField} from '../../common/hook-form'
import {getMerchant} from '../../helpers/ReduxHelpers'
import withLayout from '../../hocs/Layout'
import Loader from '../../common/Loader'
import {useGetCouriersQuery} from '../../redux/commonRTKPersist'
import {bucketForServiceType} from '../dashboard/courierPerformance/performanceUtils'
import PageTitle from '../../common/PageTitle'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, WHITE, SUBTLE, PRIMARY_BLUE, SUCCESS, WARNING} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'
import {pageContainerSx, pageHeroSx, heroTitleSx, heroSubSx, kpiCardSx, helpTextSx, helpTextTypeSx, btnRowSx} from '../../styles/patterns'

const kpiIconSx = (accent) => ({
  width: 40, height: 40, borderRadius: '10px', bgcolor: `${accent}12`,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: accent, flexShrink: 0, '& svg': {fontSize: 22},
})

const sectionSx = {bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: SHADOW.card, overflow: 'hidden', mb: 2}

const ruleCardSx = {
  border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', mb: 1.5,
  boxShadow: SHADOW.card, transition: 'box-shadow 0.2s',
  '&:hover': {boxShadow: SHADOW.cardHover},
}

const ruleHeaderSx = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  px: 2, py: 1.2, bgcolor: '#f8f9fc', borderBottom: `1px solid ${BORDER}`,
}

const conditionChipSx = {
  fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, height: 26,
  bgcolor: '#E8EAF6', color: '#283593', mr: 0.5, mb: 0.5,
}

const priorityChipSx = (i) => ({
  fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, height: 26,
  bgcolor: i === 0 ? '#E8F5E9' : i === 1 ? '#FFF3E0' : '#f5f5f5',
  color: i === 0 ? '#2E7D32' : i === 1 ? '#E65100' : SUBTLE,
  mr: 0.5, mb: 0.5,
})

const ZONES = ['LOCAL', 'REGIONAL', 'METROS', 'ROI', 'SPECIAL']

// Human-readable label for a rule condition fact (mirrors the admin allocation view),
// so the full depth — pickup pincode, invoice value, payment mode, etc. — reads clearly
// instead of raw enum names.
const factLabel = (fact = '', operator = '') => {
  const isMin = ['GREATER_THAN', 'GREATER_THAN_EQUAL', '>', '>='].includes(operator)
  const isMax = ['LESS_THAN', 'LESS_THAN_EQUAL', '<', '<='].includes(operator)
  if (fact === 'WEIGHT') return isMin ? 'Min Weight' : isMax ? 'Max Weight' : 'Weight'
  if (fact === 'ORDER_VALUE') return isMin ? 'Min Invoice Value' : isMax ? 'Max Invoice Value' : 'Invoice Value'
  const MAP = {
    ZONE: 'Zone', DELIVERY_TYPE: 'Delivery Type', SHIPMENT_TYPE: 'Shipment Type',
    PAYMENT_MODE: 'Payment Mode', CHANNEL_NAME: 'Channel', DISPATCH_MODE: 'Dispatch Mode',
    QC_TYPE: 'QC Type', PICKUP_PINCODE_PART1: 'Pickup Pincode (Part 1)',
    PICKUP_PINCODE_PART2: 'Pickup Pincode (Part 2)', DROP_PINCODE_PART1: 'Drop Pincode (Part 1)',
    DROP_PINCODE_PART2: 'Drop Pincode (Part 2)',
  }
  return MAP[fact] || fact.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const factValue = (fact, value = '') => {
  if (value === null || value === undefined || value === '') return '—'
  if (fact === 'WEIGHT') return `${Number(value).toLocaleString()} kg`
  if (fact === 'ORDER_VALUE') return `₹${Number(value).toLocaleString()}`
  return String(value).includes(',') ? String(value).split(',').map((v) => v.trim()).join(', ') : value
}

function Rules(props) {
  // Vertical split (set per menu entry): B2C = D2C, B2B = PTL
  const freightType = props.freightType || 'B2C'
  const vLabel = freightType === 'B2B' ? 'PTL' : 'D2C'
  const [data, setData] = useState([])
  const [rules, setRules] = useState([])
  const [filtersApplied, setFiltersApplied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [expandedRule, setExpandedRule] = useState(null)
  const methods = useForm({defaultValues: {Delivery_Type: '', Weight: '', ZoneName: '', ServiceType: ''}})
  const merchant = getMerchant()
  const merchantId = merchant?.id || ''
  const {data: allCouriers = []} = useGetCouriersQuery()
  const {handleSubmit, reset} = methods

  useEffect(() => {
    if (merchantId) {
      setLoading(true)
      get(`${apiConstants.BASE_LOCAL}${apiConstants.GET_MERCHANT}/${merchantId}`)
        .then((res) => { setData(res?.data); setLoading(false) })
        .catch(() => setLoading(false))
    }
  }, [merchantId])

  // Courier priority label = parent + child service (e.g. "Delhivery — Surface"), used
  // by both the on-screen Courier Priority chips and the download so they match. Reuses the
  // same code->bucket classification as courier-performance, rather than showing the raw
  // serviceTypePrimary code (e.g. "FWD_SURFACE_REG") verbatim.
  const BUCKET_LABEL = {
    SURFACE: 'Surface', AIR: 'Air', SDD: 'SDD',
    SAME_CITY_NDD: 'NDD', INTERCITY_NDD: 'NDD', REVERSE: 'Reverse',
  }
  const getCourierServiceLabel = (code) => {
    const c = allCouriers.find((cp) => cp.cpAccountCode === code)
    if (!c) return code
    const bucket = bucketForServiceType(c.serviceTypePrimary, c.dispatchMode)
    const serviceLabel = BUCKET_LABEL[bucket]
    return serviceLabel ? `${c.parent} — ${serviceLabel}` : c.parent
  }

  // PTL view = rules tagged SHIPMENT_TYPE=B2B; D2C view = everything else (untyped + B2C).
  const processedRules = (data?.mappedOrderRule?.rules || []).map((item) => {
    const zone = item.conditions?.find((d) => d.fact === 'ZONE')?.value
    const delivery = item.conditions?.find((d) => d.fact === 'DELIVERY_TYPE')?.value
    const weights = item.conditions?.filter((d) => d.fact === 'WEIGHT') || []
    // Append the unit -- matches factValue()'s WEIGHT formatting used elsewhere on this same
    // page, so a weight reads as "40 kg" everywhere instead of an ambiguous bare number here.
    const weightKey = weights.length === 2
      ? `${weights[0].operator}${weights[0].value} kg & ${weights[1].operator}${weights[1].value} kg`
      : weights.length === 1 ? `${weights[0].operator}${weights[0].value} kg` : ''
    const isB2B = item.conditions?.some((d) => d.fact === 'SHIPMENT_TYPE' && String(d.value).toUpperCase() === 'B2B')
    // Extra facts the admin download carries but the merchant export was missing (QA: Ankit).
    // Pincode facts are single large comma-joined strings; payment mode is Prepaid/COD/etc.
    const paymentMode = item.conditions?.find((d) => d.fact === 'PAYMENT_MODE')?.value ?? ''
    const pickupPincode = item.conditions?.find((d) => d.fact === 'PICKUP_PINCODE')?.value ?? ''
    const dropPincode = item.conditions?.find((d) => d.fact === 'DROP_PINCODE')?.value ?? ''
    // Distinct service-type buckets (Air/Surface/SDD/NDD) among this rule's actually configured
    // priority couriers -- used for the new Air/Surface filter, reusing the same code->bucket
    // classification (and blank-slot filtering) as the Courier Priority chips.
    const serviceTypes = new Set(
      (item.result?.values || []).filter(Boolean).map((code) => {
        const c = allCouriers.find((cp) => cp.cpAccountCode === code)
        return c ? BUCKET_LABEL[bucketForServiceType(c.serviceTypePrimary, c.dispatchMode)] : null
      }).filter(Boolean)
    )
    return {...item, zone, delivery, weightKey, isB2B, serviceTypes, paymentMode, pickupPincode, dropPincode}
  }).filter((r) => (freightType === 'B2B' ? r.isB2B : !r.isB2B))
    // No `active` filter: the admin panel (the QA reference) lists every rule in
    // mappedOrderRule.rules with no active check, so filtering here made the merchant's
    // "Total Rules" / "Couriers Active" counts lower than admin's. Only the D2C/PTL vertical
    // split above narrows the set now — matching admin within each vertical.

  // Filter options
  const weightOptions = [...new Set(processedRules.map((r) => r.weightKey).filter(Boolean))].map((w) => ({label: w, value: w}))
  const deliveryOptions = [...new Set(processedRules.map((r) => r.delivery).filter(Boolean))].map((d) => ({label: d, value: d}))
  const serviceTypeOptions = [...new Set(processedRules.flatMap((r) => [...r.serviceTypes]))].map((s) => ({label: s, value: s}))

  const onSubmit = (filters) => {
    const filtered = processedRules.filter((r) => {
      if (filters.ZoneName && r.zone !== filters.ZoneName) return false
      if (filters.Weight && r.weightKey !== filters.Weight) return false
      if (filters.Delivery_Type && r.delivery !== filters.Delivery_Type) return false
      if (filters.ServiceType && !r.serviceTypes.has(filters.ServiceType)) return false
      return true
    })
    setRules(filtered)
    setFiltersApplied(true)
  }

  // Whether a filter is active, tracked explicitly rather than inferred from rules.length --
  // a filter that legitimately matches zero rules must still show zero, not fall back to
  // the unfiltered list just because rules.length happens to be 0.
  const displayRules = filtersApplied ? rules : processedRules
  const ruleType = data?.mappedOrderRule?.type

  // Distinct couriers this merchant's own rules actually allocate to -- NOT the size of the
  // global courier master list (allCouriers), which has no relation to this merchant at all
  // and was the source of the "Couriers Active" count exceeding "Total Rules" bug.
  // result.values itself is padded with blank ('') slots up to the account's max priority
  // count, so those must be filtered out before counting -- otherwise '' counts as one
  // "distinct courier".
  const activeCourierCount = new Set(
    processedRules.flatMap((r) => (r.result?.values || []).filter(Boolean))
  ).size

  // Excel export
  // Priority columns are sized to the actual configured priorities, not the raw (blank-padded)
  // array length -- the export used to always emit as many "Priority N" columns as the widest
  // padded array (e.g. 19), so a merchant using only 7 priorities still got 12 blank ("-")
  // columns. Only as many columns as the longest genuinely-configured rule uses are generated.
  const maxPriorities = Math.max(0, ...displayRules.map((r) => (r.result?.values || []).filter(Boolean).length))
  const sheets = [{
    name: 'Allocation Rules',
    data: displayRules,
    columns: [
      {label: 'Zone', value: (r) => r.zone},
      {label: 'Weight Slab', value: (r) => r.weightKey},
      {label: 'Delivery Type', value: (r) => r.delivery},
      // Pincode lists can exceed Excel's 32,767-char cell limit, so split into two parts —
      // same as the admin download so the two files line up column-for-column.
      {label: 'Pickup Pincode Part 1', value: (r) => String(r.pickupPincode ?? '').slice(0, 32767)},
      {label: 'Pickup Pincode Part 2', value: (r) => String(r.pickupPincode ?? '').slice(32767, 65534)},
      {label: 'Drop Pincode Part 1', value: (r) => String(r.dropPincode ?? '').slice(0, 32767)},
      {label: 'Drop Pincode Part 2', value: (r) => String(r.dropPincode ?? '').slice(32767, 65534)},
      {label: 'Payment Mode', value: (r) => r.paymentMode || ''},
      ...Array.from({length: maxPriorities}, (_, i) => ({
        label: `Priority ${i + 1}`,
        value: (r) => {
          const configured = (r.result?.values || []).filter(Boolean)
          // Show parent + service type (e.g. "Delhivery — Surface"), matching the
          // Courier Priority chips in the accordions, instead of the parent name only.
          return configured[i] ? getCourierServiceLabel(configured[i]) : '-'
        },
      })),
    ],
  }]

  return (
    <Box sx={pageContainerSx}>
      <PageTitle>{vLabel} Shipping Rules</PageTitle>
      {loading && <Loader />}


      {/* KPI stats */}
      <Grid container spacing={2} sx={{mb: 2.5}}>
        {[
          {label: 'Total Rules', value: processedRules.length, color: PRIMARY_BLUE, icon: <RuleOutlinedIcon />},
          {label: 'Rule Type', value: ruleType || '—', color: SUCCESS, icon: <TuneOutlinedIcon />},
          {label: 'Couriers Active', value: activeCourierCount, color: WARNING, icon: <LocalShippingOutlinedIcon />},
          {label: 'Zones Covered', value: ZONES.length, color: SUBTLE, icon: <MapOutlinedIcon />},
        ].map((s) => (
          <Grid item xs={12} sm={6} md={3} key={s.label}>
            <Box sx={kpiCardSx(s.color)}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                <Box sx={kpiIconSx(s.color)}>{s.icon}</Box>
                <Box>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em'}}>
                    {s.label}
                  </Typography>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xl'], fontWeight: WEIGHT.extra, color: NAVY, lineHeight: 1.2}}>
                    {s.value}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Help text */}
      <Box sx={helpTextSx}>
        <InfoOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE, mt: 0.2}} />
        <Typography sx={helpTextTypeSx}>
          <strong>How allocation works:</strong> When a shipment is created, ProShip's PACE engine evaluates these rules top-to-bottom.
          The first rule whose conditions match (zone + weight + delivery type) determines the courier priority order.
          Priority 1 courier is tried first; if unavailable, Priority 2 is used, and so on.
        </Typography>
      </Box>

      {/* Filters + Export */}
      {ruleType === 'CUSTOM' && (
        <Box sx={sectionSx}>
          <Box sx={{px: 2.5, py: 2}}>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <RHFSelectField name='ZoneName' label='Zone' width='100%'
                    options={ZONES.map((z) => ({label: _.startCase(z), value: z}))} />
                </Grid>
                {weightOptions.length > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <RHFSelectField name='Weight' label='Weight Slab' width='100%' options={weightOptions} />
                  </Grid>
                )}
                {deliveryOptions.length > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <RHFSelectField name='Delivery_Type' label='Delivery Type' width='100%' options={deliveryOptions} />
                  </Grid>
                )}
                {serviceTypeOptions.length > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <RHFSelectField name='ServiceType' label='Air / Surface / SDD / NDD' width='100%' options={serviceTypeOptions} />
                  </Grid>
                )}
              </Grid>

              {/* Action row lives outside the filter grid so it doesn't wrap/shift with the
                  conditionally-rendered dropdowns above, and separates the two distinct actions
                  (apply/reset filters vs export) instead of cramming them into one grid cell. */}
              <Box sx={{...btnRowSx, justifyContent: 'space-between', mt: 2, pt: 2, borderTop: `1px solid ${BORDER}`}}>
                <Box sx={{display: 'flex', gap: 1}}>
                  <Button type='submit' variant='contained' sx={{fontFamily: FONT, textTransform: 'none', borderRadius: '8px', bgcolor: PRIMARY_BLUE, height: 36}}>
                    Filter
                  </Button>
                  <Button type='button' variant='outlined' color='error' sx={{fontFamily: FONT, textTransform: 'none', borderRadius: '8px', height: 36}}
                    onClick={() => { reset({Delivery_Type: '', Weight: '', ZoneName: '', ServiceType: ''}); setRules([]); setFiltersApplied(false) }}>
                    Reset
                  </Button>
                </Box>
                <Box sx={{'& button': {height: 36, borderRadius: '8px', fontFamily: FONT, fontSize: '0.8rem', textTransform: 'none', px: 1.75}}}>
                  <DownloadExcelMultipleSheets sheets={sheets} filename='Allocation Rules' />
                </Box>
              </Box>
            </FormProvider>
          </Box>
        </Box>
      )}

      {/* Rules list */}
      <Typography sx={{fontFamily: FONT, fontSize: '0.9rem', fontWeight: 700, color: NAVY, mb: 1.5}}>
        {displayRules.length} Rule{displayRules.length !== 1 ? 's' : ''} {filtersApplied ? '(Filtered)' : ''}
      </Typography>

      {displayRules.map((rule, i) => (
        <Box key={i} sx={ruleCardSx}>
          <Box sx={ruleHeaderSx} onClick={() => setExpandedRule(expandedRule === i ? null : i)} style={{cursor: 'pointer'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
              <Box sx={{width: 28, height: 28, borderRadius: '50%', bgcolor: PRIMARY_BLUE, color: WHITE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, fontFamily: FONT}}>
                {i + 1}
              </Box>
              <Box>
                <Typography sx={{fontFamily: FONT, fontSize: '0.85rem', fontWeight: 700, color: NAVY}}>
                  {rule.zone || 'All Zones'} · {rule.delivery || 'All Types'} · {rule.weightKey || 'All Weights'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Chip label={`${(rule.result?.values || []).filter(Boolean).length} couriers`} size='small' sx={{fontFamily: FONT, fontSize: '0.72rem', fontWeight: 600}} />
              {expandedRule === i ? <ExpandLessIcon sx={{color: SUBTLE}} /> : <ExpandMoreIcon sx={{color: SUBTLE}} />}
            </Box>
          </Box>
          <Collapse in={expandedRule === i}>
            <Box sx={{px: 2, py: 1.5}}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={5}>
                  <Typography sx={{fontFamily: FONT, fontSize: '0.78rem', fontWeight: 700, color: SUBTLE, textTransform: 'uppercase', mb: 1}}>
                    Conditions
                  </Typography>
                  {rule.conditions?.map((con, j) => (
                    <Chip key={j} label={`${factLabel(con.fact, con.operator)}: ${factValue(con.fact, con.value)}`} sx={conditionChipSx} />
                  ))}
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Typography sx={{fontFamily: FONT, fontSize: '0.78rem', fontWeight: 700, color: SUBTLE, textTransform: 'uppercase', mb: 1}}>
                    Courier Priority
                  </Typography>
                  {/* result.values itself comes padded with blank ('') slots up to the
                     account's max priority count -- filter those out so only the priorities
                     actually configured for this rule render, instead of a blank chip per
                     unused slot (e.g. P8..P19 when only 7 are configured). */}
                  {(rule.result?.values || []).filter(Boolean).map((code, k) => (
                    <Chip key={k} label={`P${k + 1}: ${getCourierServiceLabel(code)}`} sx={priorityChipSx(k)} />
                  ))}
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>
      ))}

      {processedRules.length === 0 && !loading && (
        <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', p: 4, textAlign: 'center'}}>
          <RuleOutlinedIcon sx={{fontSize: 48, color: BORDER, mb: 1}} />
          <Typography sx={{fontFamily: FONT, fontSize: '1rem', fontWeight: 700, color: SUBTLE}}>No {vLabel} Allocation Rules Configured</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: '0.85rem', color: SUBTLE, mt: 0.5}}>
            Contact your account manager to set up {vLabel} courier allocation rules for your shipments
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default withLayout(Rules)
